<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-message-text-outline me-2"></i>{{ $t('page.sms_outbox', 'SMS Outbox') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-primary" @click="openCompose"><i class="mdi mdi-plus me-1"></i>Compose</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filters.status='sent'; loadAll()" :class="{ selected: filters.status==='sent' }">
          <div class="hms-stat-num hms-stat-success">{{ stats.sent_today || 0 }}</div>
          <div class="hms-stat-lbl">Sent Today</div>
        </div>
        <div class="hms-stat" @click="filters.status='failed'; loadAll()" :class="{ selected: filters.status==='failed' }">
          <div class="hms-stat-num hms-stat-danger">{{ stats.failed_today || 0 }}</div>
          <div class="hms-stat-lbl">Failed Today</div>
        </div>
        <div class="hms-stat" @click="filters.status='pending'; loadAll()" :class="{ selected: filters.status==='pending' }">
          <div class="hms-stat-num hms-stat-warning">{{ stats.pending || 0 }}</div>
          <div class="hms-stat-lbl">Pending</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ stats.sent_30d || 0 }}</div>
          <div class="hms-stat-lbl">Sent (30d)</div>
        </div>
        <div class="hms-stat" @click="tab='templates'" :class="{ selected: tab==='templates' }">
          <div class="hms-stat-num hms-stat-info">{{ templates.length }}</div>
          <div class="hms-stat-lbl">Templates</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='log' }"       @click="tab='log'"><i class="mdi mdi-message-outline me-1"></i>Sent Log</button>
        <button class="hms-tab" :class="{ active: tab==='templates' }" @click="tab='templates'"><i class="mdi mdi-text-box-outline me-1"></i>Templates</button>
      </div>

      <!-- LOG TAB -->
      <div v-if="tab === 'log'" class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="filters.search" placeholder="Search phone, name, message..." @input="debouncedLoad" />
          </div>
          <select class="hms-input hms-filter" v-model="filters.status" @change="loadAll">
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <select class="hms-input hms-filter" v-model="filters.template_code" @change="loadAll">
            <option value="">All templates</option>
            <option v-for="t in templates" :key="t.template_id" :value="t.template_code">{{ t.name }}</option>
          </select>
          <span class="hms-muted ms-auto">{{ logs.length }} messages</span>
        </div>

        <div v-if="loading && !logs.length" class="hms-spinner"><div class="spinner-border"></div></div>
        <div v-else-if="logs.length" class="hms-msg-list">
          <div v-for="m in logs" :key="m.sms_id" class="hms-msg" :class="'st-' + m.status">
            <div class="hms-msg-head">
              <span class="hms-status-dot" :class="'dot-' + m.status"></span>
              <span class="hms-mono fw-semibold">{{ m.phone_number }}</span>
              <span v-if="m.patient_name" class="hms-muted-sm">· {{ m.patient_name }} <span class="hms-mono">{{ m.patient_no }}</span></span>
              <span v-if="m.template_name" class="hms-badge hms-badge-info">{{ m.template_name }}</span>
              <span class="hms-badge ms-auto" :class="statusBadge(m.status)">{{ m.status }}</span>
              <span class="hms-muted-sm">{{ relativeTime(m.created_at) }}</span>
            </div>
            <div class="hms-msg-body">{{ m.message }}</div>
            <div class="hms-msg-foot">
              <span class="hms-muted-sm" v-if="m.sent_by_name"><i class="mdi mdi-account-outline"></i> {{ m.sent_by_name }}</span>
              <span class="hms-muted-sm" v-if="m.sender_id"><i class="mdi mdi-tag-outline"></i> {{ m.sender_id }}</span>
              <span class="hms-muted-sm" v-if="m.sent_time"><i class="mdi mdi-clock-check-outline"></i> Sent {{ formatDateTime(m.sent_time) }}</span>
              <span class="hms-muted-sm" v-if="m.provider_response && m.status === 'failed'" :title="m.provider_response">
                <i class="mdi mdi-alert-circle-outline text-danger"></i> Provider error
              </span>
              <button v-if="m.status === 'pending' || m.status === 'failed'" class="hms-btn hms-btn-sm hms-btn-amber ms-auto" @click="retrySend(m)">
                <i class="mdi mdi-refresh me-1"></i>Retry
              </button>
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-message-off-outline"></i>No SMS messages match.</div>
      </div>

      <!-- TEMPLATES TAB -->
      <div v-if="tab === 'templates'" class="hms-card">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="hms-card-title m-0"><i class="mdi mdi-text-box-outline me-1"></i>Message Templates</div>
          <button class="hms-btn hms-btn-primary" @click="openTemplateForm()"><i class="mdi mdi-plus me-1"></i>New Template</button>
        </div>

        <div v-if="templates.length" class="hms-tpl-grid">
          <div v-for="t in templates" :key="t.template_id" class="hms-tpl">
            <div class="hms-tpl-head">
              <span class="hms-mono">{{ t.template_code }}</span>
              <span class="hms-badge ms-auto" :class="categoryBadge(t.category)">{{ t.category }}</span>
              <span class="hms-badge" :class="t.is_active == 1 ? 'hms-badge-success' : 'hms-badge-neutral'">{{ t.is_active == 1 ? 'Active' : 'Inactive' }}</span>
            </div>
            <div class="hms-tpl-name">{{ t.name }}</div>
            <div class="hms-tpl-body">{{ t.body }}</div>
            <div class="hms-tpl-vars">{{ extractVars(t.body).join(' · ') || '(no variables)' }}</div>
            <div class="d-flex gap-1 mt-2">
              <button class="hms-btn hms-btn-sm hms-btn-blue" @click="useTemplate(t)"><i class="mdi mdi-send me-1"></i>Use</button>
              <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openTemplateForm(t)"><i class="mdi mdi-pencil me-1"></i>Edit</button>
              <button class="hms-btn hms-btn-sm hms-btn-danger" @click="deleteTemplate(t)"><i class="mdi mdi-trash-can-outline me-1"></i>Delete</button>
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-text-box-remove-outline"></i>No templates yet.</div>
      </div>

      <!-- COMPOSE MODAL -->
      <div class="hms-modal-overlay" v-if="showCompose" @click.self="showCompose=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-send-outline me-1"></i>Compose SMS</span>
            <button class="hms-modal-close" @click="showCompose=false">&times;</button>
          </div>
          <div v-if="composeError" class="hms-alert hms-alert-danger">{{ composeError }}</div>

          <div v-if="!gatewayConfigured" class="hms-info-banner mb-3" style="background: rgba(255,193,7,.1); color: #ca8a04; border-color: rgba(255,193,7,.3);">
            <i class="mdi mdi-alert-outline me-1"></i>
            SMS gateway not configured — messages will be queued. Set <span class="hms-mono">sms_api_key</span> in <a href="javascript:void(0)" @click="$router.push('/settings'); showCompose=false">Settings → Advanced</a> to actually send.
          </div>

          <div class="hms-tabs hms-tabs-sm mb-3">
            <button class="hms-tab" :class="{ active: composeMode === 'single' }" @click="composeMode='single'"><i class="mdi mdi-account-outline me-1"></i>Single</button>
            <button class="hms-tab" :class="{ active: composeMode === 'bulk' }"   @click="composeMode='bulk'"><i class="mdi mdi-account-group-outline me-1"></i>Bulk</button>
          </div>

          <label class="hms-label">Template (optional)</label>
          <select class="hms-input mb-3" v-model="compose.template_code" @change="onTemplateChange">
            <option value="">— Free text —</option>
            <option v-for="t in templates.filter(x => x.is_active == 1)" :key="t.template_id" :value="t.template_code" :data-id="t.template_id">{{ t.name }}</option>
          </select>

          <!-- Single mode -->
          <div v-if="composeMode === 'single'" class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Patient (optional)</label>
              <input class="hms-input" v-model="patientSearch" placeholder="Search by name or phone..." @input="debouncedPatientSearch" />
              <div v-if="patientResults.length" class="hms-search-results">
                <div v-for="p in patientResults" :key="p.patient_id" class="hms-search-result" @click="pickPatient(p)">
                  <div class="fw-semibold">{{ p.first_name }} {{ p.last_name }}</div>
                  <div class="hms-muted-sm">{{ p.patient_no }} · {{ p.phone || '—' }}</div>
                </div>
              </div>
              <div v-if="compose.patient_id" class="hms-muted-sm mt-1">
                <i class="mdi mdi-check"></i> {{ pickedPatientLabel }} <a href="javascript:void(0)" @click="clearPatient" class="hms-link-sm">clear</a>
              </div>
            </div>
            <div class="col-md-6"><label class="hms-label">Phone *</label>
              <input class="hms-input" v-model="compose.phone_number" placeholder="254722000000" />
            </div>
          </div>

          <!-- Bulk mode -->
          <div v-if="composeMode === 'bulk'" class="mb-3">
            <label class="hms-label">Recipients</label>
            <div class="hms-bulk-bar mb-2">
              <button class="hms-btn hms-btn-sm hms-btn-blue" @click="bulkPickAppointmentsTomorrow"><i class="mdi mdi-calendar me-1"></i>All appointments tomorrow</button>
              <button class="hms-btn hms-btn-sm hms-btn-blue" @click="bulkPickArtPatients"><i class="mdi mdi-pill me-1"></i>HIV patients (ART)</button>
              <button class="hms-btn hms-btn-sm hms-btn-blue" @click="bulkPickAdmitted"><i class="mdi mdi-bed me-1"></i>Currently admitted</button>
            </div>
            <div class="hms-bulk-input">
              <input class="hms-input mb-2" v-model="bulkSearch" placeholder="Add by patient name / phone..." @input="debouncedPatientSearch" />
              <div v-if="patientResults.length" class="hms-search-results">
                <div v-for="p in patientResults" :key="p.patient_id" class="hms-search-result" @click="addBulkPatient(p)">
                  <div class="fw-semibold">{{ p.first_name }} {{ p.last_name }}</div>
                  <div class="hms-muted-sm">{{ p.patient_no }} · {{ p.phone || 'no phone' }}</div>
                </div>
              </div>
            </div>
            <div class="hms-bulk-chips" v-if="bulkPatients.length">
              <span v-for="p in bulkPatients" :key="p.patient_id" class="hms-chip">
                {{ p.first_name }} {{ p.last_name }}
                <i class="mdi mdi-close" @click="removeBulkPatient(p)"></i>
              </span>
            </div>
            <div class="hms-muted-sm mt-1">{{ bulkPatients.length }} recipients · {{ bulkPatients.filter(p => p.phone).length }} with phone numbers</div>
          </div>

          <label class="hms-label">Message *</label>
          <textarea class="hms-input mb-2" rows="4" v-model="compose.message" placeholder="Type the message... use {patient_name}, {hospital_name}, {paybill} etc."></textarea>
          <div class="hms-msg-meter mb-3">
            <span :class="messageLength > 160 ? 'hms-len-warn' : 'hms-muted-sm'">{{ messageLength }}/160 chars · {{ messageSegments }} segment{{ messageSegments === 1 ? '' : 's' }}</span>
            <button class="hms-btn hms-btn-sm hms-btn-blue ms-auto" @click="previewMessage" v-if="compose.template_code"><i class="mdi mdi-eye-outline me-1"></i>Preview substituted</button>
          </div>

          <div v-if="previewedText" class="hms-preview mb-3">
            <div class="hms-section-label">PREVIEW</div>
            <div class="hms-preview-text">{{ previewedText }}</div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="submitSend" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Sending...' : (composeMode === 'bulk' ? 'Send to ' + bulkPatients.length + ' recipients' : 'Send SMS') }}
          </button>
        </div>
      </div>

      <!-- TEMPLATE FORM MODAL -->
      <div class="hms-modal-overlay" v-if="showTemplateForm" @click.self="showTemplateForm=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-text-box-edit-outline me-1"></i>{{ tplForm.template_id ? 'Edit Template' : 'New Template' }}</span>
            <button class="hms-modal-close" @click="showTemplateForm=false">&times;</button>
          </div>
          <div v-if="tplError" class="hms-alert hms-alert-danger">{{ tplError }}</div>

          <label class="hms-label">Code *</label>
          <input class="hms-input mb-3" v-model="tplForm.template_code" :disabled="!!tplForm.template_id" placeholder="apt_reminder" />
          <label class="hms-label">Display Name *</label>
          <input class="hms-input mb-3" v-model="tplForm.name" placeholder="Appointment reminder" />
          <label class="hms-label">Category</label>
          <select class="hms-input mb-3" v-model="tplForm.category">
            <option value="appointment">Appointment</option>
            <option value="clinical">Clinical</option>
            <option value="billing">Billing</option>
            <option value="admin">Admin</option>
            <option value="marketing">Marketing</option>
            <option value="other">Other</option>
          </select>
          <label class="hms-label">Body *</label>
          <textarea class="hms-input mb-2" rows="5" v-model="tplForm.body" placeholder="Hi {patient_name}, your appointment at {hospital_name}..."></textarea>
          <div class="hms-muted-sm mb-3">Available: {patient_name} {patient_no} {hospital_name} {hospital_phone} {paybill}</div>
          <label class="hms-toggle mb-3">
            <input type="checkbox" :checked="tplForm.is_active == 1" @change="tplForm.is_active = $event.target.checked ? 1 : 0" />
            <span>Active</span>
          </label>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showTemplateForm=false">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="saveTemplate" :disabled="processing">Save</button>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.text-danger { color: var(--hms-danger); }
.hms-link-sm { color: var(--hms-primary); font-size: 11px; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-stat-success { color: var(--hms-success); }
.hms-stat-danger  { color: var(--hms-danger); }
.hms-stat-warning { color: var(--hms-warning); }
.hms-stat-info    { color: var(--hms-info); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tabs-sm .hms-tab { padding: 4px 12px; font-size: 12px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-card-title { color: var(--hms-h); font-weight: 700; font-size: 14px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input:disabled { background: var(--hms-bg3); color: var(--hms-m); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

/* Messages */
.hms-msg-list { display: flex; flex-direction: column; gap: 8px; }
.hms-msg { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-br); border-radius: 10px; padding: 12px 14px; }
.hms-msg.st-sent     { border-left-color: var(--hms-success); }
.hms-msg.st-failed   { border-left-color: var(--hms-danger); }
.hms-msg.st-pending  { border-left-color: var(--hms-warning); }
.hms-msg-head { display: flex; gap: 8px; align-items: center; margin-bottom: 6px; flex-wrap: wrap; }
.hms-status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.hms-status-dot.dot-sent    { background: var(--hms-success); }
.hms-status-dot.dot-failed  { background: var(--hms-danger); }
.hms-status-dot.dot-pending { background: var(--hms-warning); }
.hms-msg-body { color: var(--hms-h); font-size: 13px; line-height: 1.5; padding: 8px 12px; background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; white-space: pre-wrap; }
.hms-msg-foot { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; padding-top: 8px; margin-top: 8px; border-top: 1px dashed var(--hms-br); }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: var(--hms-danger); color: #fff; }

/* Templates */
.hms-tpl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.hms-tpl { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 14px; display: flex; flex-direction: column; gap: 6px; }
.hms-tpl-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.hms-tpl-name { color: var(--hms-h); font-weight: 700; font-size: 14px; }
.hms-tpl-body { color: var(--hms-s); font-size: 13px; line-height: 1.4; padding: 8px 10px; background: var(--hms-bg1); border-radius: 6px; }
.hms-tpl-vars { color: var(--hms-m); font-size: 11px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

/* Compose */
.hms-search-results { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; margin-top: 6px; max-height: 200px; overflow-y: auto; }
.hms-search-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--hms-br); }
.hms-search-result:last-child { border-bottom: none; }
.hms-search-result:hover { background: var(--hms-primary-lt); }

.hms-bulk-bar { display: flex; gap: 6px; flex-wrap: wrap; }
.hms-bulk-chips { display: flex; gap: 6px; flex-wrap: wrap; padding: 8px 0; }
.hms-chip { background: var(--hms-primary-lt); color: var(--hms-primary); border-radius: 50px; padding: 3px 10px; font-size: 11px; display: inline-flex; align-items: center; gap: 4px; }
.hms-chip i { cursor: pointer; }
.hms-chip i:hover { color: var(--hms-danger); }

.hms-msg-meter { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.hms-len-warn { color: var(--hms-warning); font-weight: 600; font-size: 12px; }

.hms-preview { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 10px 14px; }
.hms-preview-text { color: var(--hms-h); font-size: 13px; white-space: pre-wrap; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 6px; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-sm { max-width: 480px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-info-banner a { color: inherit; text-decoration: underline; font-weight: 700; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "SmsOutbox",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      tab: 'log',
      logs: [], stats: {}, templates: [],
      filters: { status: '', template_code: '', search: '' },
      _searchTimer: null,

      // Compose
      showCompose: false, composeMode: 'single', composeError: '',
      compose: { phone_number: '', message: '', template_code: '', patient_id: '' },
      patientSearch: '', patientResults: [], pickedPatientLabel: '',
      bulkSearch: '', bulkPatients: [],
      previewedText: '',
      gatewayConfigured: false,

      // Template form
      showTemplateForm: false, tplError: '',
      tplForm: { template_id: null, template_code: '', name: '', category: 'other', body: '', is_active: 1 },

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    messageLength() { return (this.compose.message || '').length; },
    messageSegments() { return Math.max(1, Math.ceil(this.messageLength / 160)); }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.loadAll();
    this.loadTemplates();
    this.checkGateway();
  },
  beforeUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => self.toastMsg = '', 2400);
    },
    debouncedLoad() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var self = this;
      this._searchTimer = setTimeout(() => self.loadAll(), 350);
    },
    async loadAll() {
      this.loading = true;
      try {
        var r = await executeJson(Object.assign({ function: "getSmsLogs" }, this.filters), hms);
        if (r.data && r.data.success) {
          this.logs  = (r.data.data && r.data.data.Logs) || [];
          this.stats = (r.data.data && r.data.data.Stats) || {};
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadTemplates() {
      try {
        var r = await executeJson({ function: "getSmsTemplates" }, hms);
        if (r.data && r.data.success) this.templates = (r.data.data && r.data.data.Templates) || [];
      } catch(e) {}
    },
    async checkGateway() {
      try {
        var r = await executeJson({ function: "getSystemConfig" }, hms);
        if (r.data && r.data.success) {
          var cfg = (r.data.data && r.data.data.Config) || [];
          var key = cfg.find(c => c.config_key === 'sms_api_key');
          this.gatewayConfigured = !!(key && key.has_value == 1);
        }
      } catch(e) {}
    },

    statusBadge(s) { return ({ sent:'hms-badge-success', failed:'hms-badge-danger', pending:'hms-badge-warning' })[s] || 'hms-badge-neutral'; },
    categoryBadge(c) { return ({ appointment:'hms-badge-info', clinical:'hms-badge-success', billing:'hms-badge-warning', admin:'hms-badge-purple', marketing:'hms-badge-info' })[c] || 'hms-badge-neutral'; },
    formatDateTime(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); },
    relativeTime(s) {
      if (!s) return ''; var d = new Date(s.replace ? s.replace(' ','T') : s); if (isNaN(d)) return s;
      var diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return Math.floor(diff) + 's ago';
      if (diff < 3600) return Math.floor(diff/60) + 'm ago';
      if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
      if (diff < 604800) return Math.floor(diff/86400) + 'd ago';
      return d.toLocaleDateString();
    },
    extractVars(body) {
      var matches = (body || '').match(/\{([a-z_]+)\}/g) || [];
      var unique = {};
      matches.forEach(m => { unique[m] = true; });
      return Object.keys(unique);
    },

    async retrySend(m) {
      try {
        var r = await executeJson({ function: "sendSms", phone_number: m.phone_number, message: m.message, patient_id: m.patient_id, template_code: m.template_code }, hms);
        if (r.data && r.data.success) { this.showToast(r.data.message || 'Sent'); await this.loadAll(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    /* ---------- Compose ---------- */
    openCompose() {
      this.composeMode = 'single';
      this.compose = { phone_number: '', message: '', template_code: '', patient_id: '' };
      this.patientSearch = ''; this.patientResults = []; this.pickedPatientLabel = '';
      this.bulkSearch = ''; this.bulkPatients = [];
      this.previewedText = '';
      this.composeError = '';
      this.showCompose = true;
    },
    debouncedPatientSearch() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var self = this;
      this._searchTimer = setTimeout(async () => {
        var q = (self.composeMode === 'bulk' ? self.bulkSearch : self.patientSearch);
        if (!q || q.length < 2) { self.patientResults = []; return; }
        try {
          var r = await executeJson({ function: "getPatients", search: q }, hms);
          if (r.data && r.data.success) self.patientResults = ((r.data.data && r.data.data.Patients) || []).slice(0, 10);
        } catch(e) {}
      }, 300);
    },
    pickPatient(p) {
      this.compose.patient_id = p.patient_id;
      this.compose.phone_number = p.phone || '';
      this.pickedPatientLabel = p.first_name + ' ' + p.last_name + ' (' + p.patient_no + ')';
      this.patientSearch = ''; this.patientResults = [];
    },
    clearPatient() { this.compose.patient_id = ''; this.pickedPatientLabel = ''; },
    addBulkPatient(p) {
      if (!this.bulkPatients.find(x => x.patient_id === p.patient_id)) this.bulkPatients.push(p);
      this.bulkSearch = ''; this.patientResults = [];
    },
    removeBulkPatient(p) { this.bulkPatients = this.bulkPatients.filter(x => x.patient_id !== p.patient_id); },

    async bulkPickAppointmentsTomorrow() {
      try {
        var r = await executeJson({ function: "getAppointments" }, hms);
        if (r.data && r.data.success) {
          var tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0,10);
          var appts = (r.data.data && r.data.data.Appointments) || [];
          var byPid = {};
          appts.filter(a => (a.appointment_date || '').slice(0,10) === tomorrow).forEach(a => {
            byPid[a.patient_id] = { patient_id: a.patient_id, first_name: a.patient_name || '', last_name: '', phone: a.phone, patient_no: a.patient_no };
          });
          this.bulkPatients = Object.values(byPid);
          this.showToast(this.bulkPatients.length + ' patient(s) loaded');
        }
      } catch(e) {}
    },
    async bulkPickArtPatients() {
      try {
        var r = await executeJson({ function: "getHivRegistry" }, hms);
        if (r.data && r.data.success) {
          var rows = (r.data.data && r.data.data.HivRegistry) || (r.data.data && r.data.data.Registry) || [];
          this.bulkPatients = rows.slice(0, 50).map(x => ({ patient_id: x.patient_id, first_name: x.patient_name || '', last_name: '', phone: x.phone, patient_no: x.patient_no }));
          this.showToast(this.bulkPatients.length + ' HIV patient(s) loaded');
        }
      } catch(e) {}
    },
    async bulkPickAdmitted() {
      try {
        var r = await executeJson({ function: "getActiveAdmissions" }, hms);
        if (r.data && r.data.success) {
          var rows = (r.data.data && r.data.data.Admissions) || [];
          this.bulkPatients = rows.map(x => ({ patient_id: x.patient_id, first_name: x.patient_name || '', last_name: '', phone: x.phone, patient_no: x.patient_no }));
          this.showToast(this.bulkPatients.length + ' admitted patient(s) loaded');
        }
      } catch(e) {}
    },

    onTemplateChange() {
      var t = this.templates.find(x => x.template_code === this.compose.template_code);
      if (t) this.compose.message = t.body;
      this.previewedText = '';
    },
    async previewMessage() {
      if (!this.compose.template_code) return;
      var tpl = this.templates.find(x => x.template_code === this.compose.template_code);
      if (!tpl) return;
      try {
        var r = await executeJson({ function: "previewSmsTemplate", template_id: tpl.template_id, patient_id: this.compose.patient_id }, hms);
        if (r.data && r.data.success) this.previewedText = (r.data.data && r.data.data.preview) || '';
      } catch(e) {}
    },
    useTemplate(t) {
      this.openCompose();
      this.compose.template_code = t.template_code;
      this.compose.message = t.body;
    },

    async submitSend() {
      this.composeError = '';
      if (!this.compose.message) { this.composeError = 'Message is required.'; return; }
      this.processing = true;
      try {
        if (this.composeMode === 'bulk') {
          var ids = this.bulkPatients.map(p => p.patient_id);
          if (!ids.length) { this.composeError = 'Pick at least one recipient.'; this.processing = false; return; }
          var r = await executeJson({ function: "bulkSendSms", message: this.compose.message, template_code: this.compose.template_code, patient_ids: ids }, hms);
          if (r.data && r.data.success) {
            this.showToast(r.data.message || 'Queued');
            this.showCompose = false;
            await this.loadAll();
          } else this.composeError = r.data.message || 'Failed';
        } else {
          if (!this.compose.phone_number) { this.composeError = 'Phone number is required.'; this.processing = false; return; }
          var r = await executeJson(Object.assign({ function: "sendSms" }, this.compose), hms);
          if (r.data && r.data.success) {
            this.showToast(r.data.message || 'Sent');
            this.showCompose = false;
            await this.loadAll();
          } else this.composeError = r.data.message || 'Failed';
        }
      } catch(e) { this.composeError = 'Connection error'; }
      this.processing = false;
    },

    /* ---------- Templates ---------- */
    openTemplateForm(t) {
      this.tplError = '';
      if (t) this.tplForm = Object.assign({}, t);
      else   this.tplForm = { template_id: null, template_code: '', name: '', category: 'other', body: '', is_active: 1 };
      this.showTemplateForm = true;
    },
    async saveTemplate() {
      this.tplError = '';
      if (!this.tplForm.template_code || !this.tplForm.name || !this.tplForm.body) {
        this.tplError = 'Code, name, and body are required.'; return;
      }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveSmsTemplate" }, this.tplForm), hms);
        if (r.data && r.data.success) { this.showToast('Template saved'); this.showTemplateForm = false; await this.loadTemplates(); }
        else this.tplError = r.data.message || 'Failed';
      } catch(e) { this.tplError = 'Connection error'; }
      this.processing = false;
    },
    async deleteTemplate(t) {
      if (!confirm('Delete template "' + t.name + '"?')) return;
      try {
        var r = await executeJson({ function: "deleteSmsTemplate", template_id: t.template_id }, hms);
        if (r.data && r.data.success) { this.showToast('Deleted'); await this.loadTemplates(); }
      } catch(e) {}
    }
  }
};
</script>
